// External Dependencies
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Center } from '@chakra-ui/react';
import {
  DisplayAggregateQuestionDto,
  QuestionComponentDto,
} from '@keyops-cep/api-client';
import { Chart } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

// Internal Dependencies
import { colors } from '../../../assets/custom-theme';
import { ISurveySparrowAnswerData } from '../../../pages/EngagementDetails/types';
import {
  CHART_COLORS,
  MIN_GRAPH_FONT_SIZE,
} from '../../../utils/constants/visualization-constants';
import {
  calculateStackedBarDatalabelFont,
  getBarDimensions,
  getChartTitleConfig,
  getQuestionGraphFontSize,
} from '../../../utils/functions/chart-label-utils';
import { formatPercent } from '../../../utils/functions/stats';
import {
  copyGraph,
  downloadGraph,
} from '../../../utils/functions/visualization-actions';
import {
  sortGraphResults,
  splitStringIntoChunks,
} from '../../../utils/functions/visualization-utils';
import { VisualizationChartRef } from '..';

type StackedBarChartProps = {
  question: DisplayAggregateQuestionDto;
  questionAnswers: (ISurveySparrowAnswerData | undefined)[];
  maintainAspectRatio: boolean | undefined;
  isExpanded: boolean | undefined;
};

// This is the limit of characters and lines for the y axis labels. If the labels get too long,
// the visualization can get messed up.
const charLabelLimit = 20;
const expandedLabelCharLimit = 40;
const expandedLabelLineLimit = 3;
const MIN_LEGEND_FONT = 12; // have to use a custom size here, as there are often several and long labels

const StackedBarChart = forwardRef<VisualizationChartRef, StackedBarChartProps>(
  (
    {
      question,
      questionAnswers,
      maintainAspectRatio = false,
      isExpanded = false,
    },
    ref
  ) => {
    const [dataset, setDataset] = useState<
      {
        label: string;
        data: number[];
        backgroundColor: string;
      }[]
    >([]);

    // Init chart
    const chartRef: { current: Chart<'bar', number[], string> | null } =
      useRef(null);
    const chartContainer = useRef<HTMLCanvasElement>(null);
    const font = getQuestionGraphFontSize(isExpanded);

    // useMemo to avoid infinite loop with questionLabels
    const questionLabels = useMemo(() => {
      return (question.choices ?? [])
        .toSorted((a, b) => a.order - b.order)
        .map((choice) => choice.label);
    }, [question.choices]);

    const { map: mapQuestionComponentsIdToIndex, sortedQuestionComponents } =
      useMemo(() => {
        const map = new Map();
        const sortedQuestionComponents = sortGraphResults(
          question.visualization,
          question.displayConfig?.orderBy,
          question.components
        ) as QuestionComponentDto[];

        sortedQuestionComponents.forEach((component, index) =>
          map.set(component.surveySparrowId, index)
        );
        return { map, sortedQuestionComponents };
      }, [
        question.components,
        question.visualization,
        question.displayConfig?.orderBy,
      ]);

    const answerComponents = useMemo(
      () => questionAnswers.map((answer) => answer?.components),
      [questionAnswers]
    );

    useEffect(() => {
      // Ex:
      // {
      //    Often: [AnswersCountForQuestionComponent1, AnswersCountForQuestionComponent2, ...],
      //    Always: [],
      //    Never: [],
      //    "Rarely (Access to a clinical immunologist is limited)": [],
      //    Sometimes: [],
      // }
      const answersByQuestionChoices: Record<string, number[]> = {};

      // Init answersByQuestionChoices
      // Each label can have answers for each question components
      questionLabels.forEach((label) => {
        // the indexes of the array should reflect the order of the question components
        answersByQuestionChoices[label] = Array(
          question.components.length
        ).fill(0);
      });

      // Fill answersByQuestionChoices
      answerComponents.forEach((singleResponse) => {
        singleResponse?.forEach((answer) => {
          const { value, questionComponentSurveySparrowId } = answer;
          // take the index of the current questionComponent that has been answered to
          // to populate answersByQuestionChoices[currentAnswerValue] at the right position
          const currentIndex = mapQuestionComponentsIdToIndex.get(
            questionComponentSurveySparrowId
          );
          const answeredLabel = value.toString();

          if (answersByQuestionChoices[answeredLabel]) {
            answersByQuestionChoices[answeredLabel][currentIndex] += 1;
          } else {
            console.warn(`Unknown answer label: ${answeredLabel}`);
          }
        });
      });

      // format new object with percentage values, looking like:
      // {
      //   Never: [0.00,50.00],
      //   "Rarely (Access to a clinical immunologist is limited)": [50.00,0.00],
      //   Sometimes: [0.00,0.00],
      //   Often: [0.00,50.00],
      //   Always: [50.00,0.00],
      // }
      const answersByQuestionChoicesPercent: Record<string, number[]> = {};
      Object.entries(answersByQuestionChoices).forEach(([label, counts]) => {
        answersByQuestionChoicesPercent[label] = counts.map((count) =>
          formatPercent(count, answerComponents.length)
        );
      });

      // populate dataset for the graph respecting the labels order
      const nextDataset = questionLabels.map((label, index) => ({
        label,
        data: answersByQuestionChoicesPercent[label],
        backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
      }));

      setDataset(nextDataset);
    }, [
      answerComponents,
      question.choices,
      questionLabels,
      question.components.length,
      mapQuestionComponentsIdToIndex,
    ]);

    // Handle download and copy of the graph
    useImperativeHandle(ref, () => ({
      onDownload: (name: string) => {
        downloadGraph(name, chartRef);
      },

      onCopyToClipboard: async () => {
        await copyGraph(chartRef);
      },
    }));

    useEffect(() => {
      if (chartContainer.current) {
        chartRef.current = new Chart<'bar', number[], string>(
          chartContainer.current,
          {
            type: 'bar',
            data: {
              labels: sortedQuestionComponents.map(
                (component) => component.label
              ),
              datasets: dataset.map((dataset) => ({
                ...dataset,
                categoryPercentage: 0.9, // closer to 1 for less space between categories
                barPercentage: 1, // closer to 1 for wider bars within their category
                maxBarThickness: isExpanded ? 130 : 50, // limit bar width
                borderRadius: (ctx) => {
                  const { height } = getBarDimensions(ctx);
                  return Math.min(
                    Math.floor(height * 0.3),
                    isExpanded ? 10 : 5
                  );
                },
              })),
            },
            options: {
              responsive: true,
              maintainAspectRatio,
              layout: {
                padding: {
                  right: 30,
                },
              },
              plugins: {
                legend: {
                  display: true, // display the question choices
                  position: 'bottom',
                  labels: {
                    boxWidth: Chart.defaults.font.size,
                    font: isExpanded
                      ? font
                      : { ...font, size: MIN_LEGEND_FONT },
                  },
                },
                tooltip: {
                  enabled: true,
                  bodyFont() {
                    return isExpanded
                      ? font
                      : { ...font, size: MIN_GRAPH_FONT_SIZE };
                  },
                  titleFont: isExpanded
                    ? font
                    : { ...font, size: MIN_GRAPH_FONT_SIZE },
                  callbacks: {
                    title: (context) => {
                      const questionChoiceLabel = context[0].dataset.label;
                      return `${questionChoiceLabel}`;
                    },
                    label: (context) => {
                      const formattedValue = context.formattedValue;
                      return ` ${parseFloat(formattedValue).toFixed(1)}%`;
                    },
                  },
                },
                datalabels: {
                  align: 'center',
                  anchor: 'center',
                  formatter: (value) => {
                    if (value === '0.00') return null;
                    return Math.round(value) + '%';
                  },
                  color: colors.keyops.arcticBlue,
                  font: (context) =>
                    calculateStackedBarDatalabelFont(context, isExpanded),
                },
                ...(question.displayConfig?.chartTitle && {
                  title: getChartTitleConfig(
                    question.displayConfig?.chartTitle,
                    isExpanded
                  ),
                }),
              },
              indexAxis: 'y', // Set the index axis to 'y' for horizontal bars
              scales: {
                // for stacked bars
                x: {
                  max: 100, // ensure x-axis caps at 100%
                  stacked: true,
                  ticks: {
                    font,
                  },
                  ...(question.displayConfig?.xAxisTitle && {
                    title: {
                      display: isExpanded,
                      text: question.displayConfig?.xAxisTitle,
                      font,
                    },
                  }),
                },
                y: {
                  stacked: true,
                  ticks: {
                    callback: function (value) {
                      if (value || value === 0) {
                        const label: string = this.getLabelForValue(
                          parseInt(value as string)
                        );
                        return label.length > charLabelLimit
                          ? isExpanded
                            ? splitStringIntoChunks(
                                label,
                                expandedLabelCharLimit,
                                expandedLabelLineLimit
                              )
                            : `${label.substring(0, charLabelLimit)}...`
                          : label;
                      }
                      return '';
                    },
                    font,
                  },
                  ...(question.displayConfig?.yAxisTitle && {
                    title: {
                      display: isExpanded,
                      text: question.displayConfig?.yAxisTitle,
                      font,
                      padding: {
                        bottom: 20,
                      },
                    },
                  }),
                },
              },
            },
            plugins: [...[ChartDataLabels]],
          }
        );
      }

      // Clean up the chart instance when the component unmounts
      return () => {
        if (chartRef.current) {
          return chartRef.current.destroy();
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [question, dataset]);

    // question.choices can be undefined
    if (!question.choices || question.choices.length === 0) {
      return <Center>No choices available for this question.</Center>;
    }

    return (
      <Center h={'100%'}>
        <canvas ref={chartContainer}></canvas>
      </Center>
    );
  }
);

StackedBarChart.displayName = 'StackedBarChart';

export default StackedBarChart;
